import React, { useEffect, useMemo, useState } from 'react';
import { AccessibilityInfo, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import PropTypes from 'prop-types';
import { getPetById, loadPetImage, loadPetStreamingImages } from '@/constants/pets';
import { useAuthStore } from '@/store';

const FRAME_DURATION = 900;

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  image: {
    width: '100%',
    height: '100%',
  },
});

function PetWaitingIcon({ size = 30, fallback = null, defaultPetId = null, style = null }) {
  const selectedPetId = useAuthStore(
    state => state.user?.preferences?.appearance?.selectedPet || null
  );
  const pet = useMemo(() => getPetById(selectedPetId) || getPetById(defaultPetId), [selectedPetId, defaultPetId]);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [images, setImages] = useState({
    default: null,
    streaming: [],
    frameIndex: 0,
  });

  useEffect(() => {
    let mounted = true;
    AccessibilityInfo.isReduceMotionEnabled().then(value => {
      if (mounted) setReduceMotion(value);
    });
    const subscription = AccessibilityInfo.addEventListener?.('reduceMotionChanged', setReduceMotion);
    return () => {
      mounted = false;
      subscription?.remove?.();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setImages({ default: null, streaming: [], frameIndex: 0 });
    if (!pet) return undefined;

    loadPetImage(pet)
      .then(image => {
        if (!cancelled) {
          setImages(current => ({ ...current, default: image }));
        }
      })
      .catch(error => console.error(`Unable to load ${pet.name}'s waiting image:`, error));

    return () => {
      cancelled = true;
    };
  }, [pet]);

  useEffect(() => {
    let cancelled = false;
    if (!pet || reduceMotion) return undefined;

    loadPetStreamingImages(pet)
      .then(streaming => {
        if (!cancelled) {
          setImages(current => ({ ...current, streaming }));
        }
      })
      .catch(error => console.error(`Unable to load ${pet.name}'s waiting animation:`, error));

    return () => {
      cancelled = true;
    };
  }, [pet, reduceMotion]);

  useEffect(() => {
    if (reduceMotion || images.streaming.length < 2) return undefined;
    const interval = setInterval(() => {
      setImages(current => ({
        ...current,
        frameIndex: (current.frameIndex + 1) % current.streaming.length,
      }));
    }, FRAME_DURATION);
    return () => clearInterval(interval);
  }, [images.streaming.length, reduceMotion]);

  const source = reduceMotion
    ? images.default || pet?.image
    : images.streaming[images.frameIndex] || images.default || pet?.image;

  if (!pet || !source) return fallback;

  return (
    <View
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      pointerEvents="none"
      style={[styles.container, { width: size, height: size }, style]}
    >
      <Image
        accessible={false}
        source={source}
        style={styles.image}
        contentFit="contain"
        transition={reduceMotion ? 0 : 80}
      />
    </View>
  );
}

PetWaitingIcon.propTypes = {
  size: PropTypes.number,
  fallback: PropTypes.node,
  defaultPetId: PropTypes.string,
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.number]),
};

export default React.memo(PetWaitingIcon);
